// Polyfill Object.hasOwn if it doesn't exist already.
import 'core-js/modules/es.object.has-own';
import 'core-js/modules/web.structured-clone';
import '../public/css/app.less';

import * as Sentry from '@sentry/vue';
import HighchartsVue from 'highcharts-vue';
import sanitizeHTML from 'sanitize-html';
import { createApp, watch } from 'vue';

import 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/accessibility';
import 'highcharts/modules/drilldown';
import 'highcharts/modules/heatmap';
import 'highcharts/modules/histogram-bellcurve';
import 'highcharts/modules/mouse-wheel-zoom';
import 'highcharts/modules/networkgraph';
import 'highcharts/modules/pattern-fill';
import 'highcharts/modules/sankey';
import 'highcharts/modules/solid-gauge';
// treemap must load before sunburst — sunburst extends the treemap series.
import 'highcharts/modules/treemap';
import 'highcharts/modules/sunburst';
import 'highcharts/modules/xrange';

import { initializeAggConnect } from '@pendo/agg-connect';
import * as pendoPermissions from '@pendo/permissions';
import { BuildingBlock } from '@pendo/services/BuildingBlocks';
import { baseUrl, gcsBucket, origin } from '@pendo/services/Environments';

import { LazyVuetifyPlugin } from '@/plugins/lazy-vuetify';
import { FlagsPlugin } from '@/plugins/flags';
import { isProdEnvironment } from '@/settings/environment.utils.js';
import { setupAnalytics } from '@/utils/analytics';
import { setupPerfume } from '@/utils/browser-performance-analytics';
import { resolveDesignerHost } from '@/utils/designer-launch.js';
import { listenToDesigner } from '@/utils/designer-listener.js';
import { initializeHttpWrapper } from '@/utils/http';
import { i18n } from '@/utils/i18n';
import { isLocaleLoaded } from '@/utils/i18n/i18n';
import { preloadRememberedLocale, refreshLocale, waitForLocale } from '@/utils/i18n/i18n-locale-resolver';
import { initSentry, attachSentryVueApp } from '@/utils/sentry';
import { initAutoReportTracker } from '@/utils/track-report';
import { isZendeskEnabledForPath, setupZendeskSnippetForPendoInternal } from '@/utils/zendesk-setup';

import router from './vue-router/router';

import pendoGlobalsPlugin from '@/plugins/app-wide-dependency-plugin';

import { pinia } from '@/pinia';
import { useConfigStore } from '@/stores/config.store';

import App from './app.vue';
import { registerPointRoleOverride } from '@/utils/point-roles.utils';

import { useAuthStore } from '@/stores/auth.store';

registerPointRoleOverride();

const vueApp = createApp(App);
// Install Pinia before bootstrap auth/config so those stores receive queued plugins.
vueApp.use(pinia);

window.designerHost = resolveDesignerHost(__PROCESS__.LOCAL_ENV, window.location.origin);

const http = initializeHttpWrapper();
initializeAggConnect({
    http,
    env: isProdEnvironment() ? 'production' : window.appEnvId,
    observe: watch,
    maxAggregationBatchSize: 10,
});

window[Symbol.for('Aggregation')].enableNamesOnRequestUrls();

preloadRememberedLocale();
useAuthStore().init();
useConfigStore().init();

if (window.Cypress) {
    // Attach Pinia and permissions to the window, which can be useful
    // for manually mocking state or permissions in tests
    window.__pinia__ = pinia;
    window.__pendoPermissions__ = pendoPermissions;
}

setupAnalytics();
setupPerfume();
listenToDesigner();
BuildingBlock.setDependencies(origin(), gcsBucket(), baseUrl());

if (!window.Cypress && isZendeskEnabledForPath(window.location.pathname)) {
    setupZendeskSnippetForPendoInternal();
}

// re-implement angular's a tag prevent default, so `<a href="#">` doesn't reload the page
document.addEventListener('click', (event) => {
    const aTag = event?.target?.closest('a');
    if (!aTag) return;

    const targetingHash = aTag.getAttribute('href') === '#';

    if (!targetingHash) return;

    event.preventDefault();
});

initAutoReportTracker(router);
if (!__PROCESS__.LOCAL_ENV) {
    initSentry(router);
}

async function configureVueApp () {
    attachSentryVueApp(vueApp);

    vueApp.config.globalProperties.$sanitize = sanitizeHTML;

    // Config change needed to make our Vue app work with the error clicks frustration type in session replay
    // See https://pendo-io.atlassian.net/wiki/spaces/ENG/pages/3446964272/Error+Clicks+Design+Doc#%E2%AD%90-Option-%233%3A-Error-event-listener-%2B-modify-Vue-config-%E2%AD%90
    vueApp.config.errorHandler = function (error, vm, info) {
        if (vm) {
            window.dispatchEvent(new CustomEvent('error', { detail: { element: vm.$el, error } }));
            Sentry.captureException(error, {
                tags: {
                    'vue.component': vm?.$options?.name || 'UnknownComponent',
                    'vue.errorContext': info,
                },
            });
        } else {
            window.dispatchEvent(new CustomEvent('error', { detail: { error } }));
            Sentry.captureException(error);
        }
        if (__PROCESS__.LOCAL_ENV) {
            console.error(error);
            throw error;
        }
    };

    vueApp.use(pendoGlobalsPlugin);
    vueApp.use(FlagsPlugin);
    vueApp.use(HighchartsVue);
    vueApp.use(LazyVuetifyPlugin);
    vueApp.use(i18n);

    if (__PROCESS__.LOCAL_ENV) {
        const params = new URLSearchParams(window.location.search);
        const feDebug = params.get('feDebug');

        if (__PROCESS__.FE_DEBUG || feDebug === 'true') {
            try {
                const VueScan = await import('z-vue-scan');
                vueApp.use(VueScan.default, {});
            } catch (error) {
                console.error('Error initializing VueScan', error);
            }
        }
    }

    if (window.Cypress) {
        vueApp.mixin({
            mounted () {
                this.$el.__vue__ = this;
            },
        });
    }
}

function mountVueApp () {
    const emptyLoadingStateId = 'empty-app-loading-state';
    try {
        const loadingEl = document.getElementById(emptyLoadingStateId);
        // Using `transitioned` is the ideal way to remove the loading state, but
        // it can't always be relied on (such as if the a11y `prefers-reduced-motion` is present)
        // Set a backup timer just in case, to make certain the loading state always gets removed
        // regardless of whatever else might cancel `transitioned`
        const timer = window.setTimeout(() => {
            const lingeringLoadingEl = document.getElementById(emptyLoadingStateId);
            if (lingeringLoadingEl) lingeringLoadingEl.remove();
        }, 600);

        loadingEl.addEventListener('transitionend', () => {
            loadingEl.remove();
            window.clearTimeout(timer);
        }, { once: true });

        loadingEl.classList.add('fade-out');
    } catch {
        // HMR can cause this to fail if the element has already been removed - thats fine
        // But absolutely do not let .mount fail
    }
    vueApp.mount('#vue-mount');
    window.performance?.mark?.('pendo:app-mounted');
}

const LOCALE_MOUNT_TIMEOUT_MS = 3000;

async function bootstrapTheApp () {
    const authStore = useAuthStore();
    const whiteLabelSettingsLoaded = authStore.fetchUnauthenticatedTrainingAttributes()
        .catch((error) => console.error('Failed to fetch unauthenticated training attributes:', error));

    window.__appLoadDate = new Date();

    const vueAppConfigured = configureVueApp();

    await Promise.all([authStore.waitForSessionLoad(), authStore.waitForSegmentFlags()]);

    vueApp.use(router);

    const routerReady = router.isReady().then(() => {
        window.performance?.mark?.('pendo:router-ready');
    });

    await Promise.all([
        window.Cypress ? null : whiteLabelSettingsLoaded,
        authStore.waitForUserLoad(),
        routerReady,
        vueAppConfigured,
    ]);

    // User load usually requests the locale, but some paths skip it (e.g. Novus- or training-only users).
    if (!isLocaleLoaded()) {
        refreshLocale();
    }

    const localeLoaded = waitForLocale();
    // Nothing to render until the first locale lands; the timeout only spares waiting on a locale switch.
    await (!isLocaleLoaded()
        ? localeLoaded
        : Promise.race([localeLoaded, new Promise((resolve) => setTimeout(resolve, LOCALE_MOUNT_TIMEOUT_MS))]));

    mountVueApp();
}

bootstrapTheApp();
